import React, { useState, useEffect } from 'react';
import { Award, ExternalLink, ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';

interface SkillsProps {
  language: string;
}

interface Certificate {
  name: string;
  issuer: string;
  year: string;
  url?: string;
}


interface Quote {
  textEn: string;
  textVi: string;
  author: string;
}

const Skills: React.FC<SkillsProps> = ({ language }) => {
  const [activeQuote, setActiveQuote] = useState<number>(0);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  useEffect(() => {
    AOS.refresh();
  }, [language]);

  const translations = {
    en: {
      title: 'My Skills',
      programmingLanguages: 'Programming Languages',
      webMobile: 'Basic Techs',
      gameGraphics: 'Game Dev',
      databases: 'Databases',
      toolsDevOps: 'Tools',
      ide: 'IDEs & Editors',
      certificates: 'Certificates',
      languages: 'Languages',
      viewCertificate: 'View Certificate',
      inspirationalQuotes: 'Inspirational Quotes',
      previous: 'Previous quote',
      next: 'Next quote',
      viewGalaxy: 'Skill Galaxy',
      viewRadar: 'Radar Map',
    },
    vi: {
      title: 'KỸ NĂNG', 
      programmingLanguages: 'Ngôn Ngữ Lập Trình',
      webMobile: 'Công Nghệ Cơ Bản',
      gameGraphics: 'Phát Triển Game',
      databases: 'Cơ Sở Dữ Liệu',
      toolsDevOps: 'Công Cụ',
      ide: 'Môi Trường Phát Triển (IDE/Editors)',
      certificates: 'Chứng Chỉ',
      languages: 'Ngôn Ngữ',
      viewCertificate: 'Xem Chứng Chỉ',
      inspirationalQuotes: 'Câu Nói Truyền Cảm Hứng',
      previous: 'Câu trước',
      next: 'Câu tiếp theo',
      viewGalaxy: 'Vũ trụ Kỹ năng',
      viewRadar: 'Bản đồ Năng lực',
    }
  };

  const t = translations[language as keyof typeof translations];

  // Cấu trúc lại các nhóm kỹ năng - Đã xóa StarUML
  const skillCategories = {
    programmingLanguages: [
      {
        name: 'C Sharp',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg" alt="C#" className="w-4 h-4" />
      },
      {
        name: 'Java',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" alt="Java" className="w-4 h-4" />
      }
    ],
    webMobile: [
      {
        name: 'HTML',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg" alt="HTML" className="w-4 h-4" />
      },
      {
        name: 'CSS',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg" alt="CSS" className="w-4 h-4" />
      },
      {
        name: 'JavaScript',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg" alt="JavaScript" className="w-4 h-4" />
      }
    ],
    gameGraphics: [
      {
        name: 'Unity',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/unity/unity-original.svg" alt="Unity" className="w-4 h-4" />
      }
    ],
    databases: [
      { 
        name: 'SQL Server', 
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/microsoftsqlserver/microsoftsqlserver-plain.svg" alt="SQL Server" className="w-4 h-4" />
      },
      { 
        name: 'MySQL', 
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" alt="MySQL" className="w-4 h-4" />
      }
    ],
    toolsDevOps: [
      {
        name: 'Git',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg" alt="Git" className="w-4 h-4" />
      },
      {
        name: 'Github',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg" alt="GitHub" className="w-4 h-4" />
      }
    ],
    ide: [
      {
        name: 'Visual Studio Code',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg" alt="Visual Studio Code" className="w-4 h-4" />
      },
      {
        name: 'Visual Studio',
        icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/visualstudio/visualstudio-plain.svg" alt="Visual Studio" className="w-4 h-4" />
      }
    ]
  };

  const certificates: Certificate[] = [
    {
      name: 'Foundational C# with Microsoft',
      issuer: 'freeCodeCamp',
      year: 'Apr 2024',
      url: 'https://www.freecodecamp.org/certification/ntainguyen/foundational-c-sharp-with-microsoft'
    },
    {
      name: 'Introduction to Databases',
      issuer: 'Meta × Coursera',
      year: 'Jun 2024',
      url: 'https://coursera.org/share/6aadd7aa1cc1ac33b32fbb15c81af2b4'
    },
    {
      name: 'SQL (Intermediate)',
      issuer: 'HackerRank',
      year: 'Jun 2026',
      url: 'https://www.hackerrank.com/certificates/5c17de6dfcbc'
    }
  ];


  const techQuotes: Quote[] = [
    {
      textEn: "Your time is limited, so don't waste it living someone else's life.",
      textVi: "Thời gian của bạn là hữu hạn, đừng lãng phí nó bằng cách sống cuộc đời của người khác.",
      author: "Steve Jobs"
    },
    {
      textEn: "The best way to predict the future is to invent it.",
      textVi: "Cách tốt nhất để dự đoán tương lai là tạo ra nó.",
      author: "Alan Kay"
    },
    {
      textEn: "Move fast and break things. Unless you are breaking stuff, you are not moving fast enough.",
      textVi: "Hãy di chuyển nhanh và phá vỡ mọi rào cản. Nếu bạn chưa phá vỡ được điều gì, nghĩa là bạn di chuyển chưa đủ nhanh.",
      author: "Mark Zuckerberg"
    },
    {
      textEn: "Innovation distinguishes between a leader and a follower.",
      textVi: "Sự đổi mới chính là ranh giới phân biệt giữa người lãnh đạo và kẻ theo sau.",
      author: "Steve Jobs"
    },
    {
      textEn: "The biggest risk is not taking any risk. In a world that's changing quickly, the only strategy that is guaranteed to fail is not taking risks.",
      textVi: "Rủi ro lớn nhất là không dám chấp nhận rủi ro. Trong một thế giới thay đổi nhanh chóng, chiến lược duy nhất đảm bảo sự thất bại là không chấp nhận rủi ro.",
      author: "Mark Zuckerberg"
    }
  ];

  const navigateQuote = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setActiveQuote(prev => (prev === 0 ? techQuotes.length - 1 : prev - 1));
    } else {
      setActiveQuote(prev => (prev === techQuotes.length - 1 ? 0 : prev + 1));
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const startTimer = () => {
      if (window.innerWidth > 768) {
        if (!timer) {
          timer = setInterval(() => {
            setActiveQuote(prev => (prev === techQuotes.length - 1 ? 0 : prev + 1));
          }, 2000);
        }
      } else {
        if (timer) {
          clearInterval(timer);
          timer = undefined as unknown as ReturnType<typeof setInterval>;
        }
      }
    };
    startTimer();
    window.addEventListener('resize', startTimer);
    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener('resize', startTimer);
    };
  }, [techQuotes.length]);

  return (
    <section id="skills" className="pt-10 pb-20 px-6 rounded-lg">
      <div className="max-w-5xl mx-auto">
        
        <div className="text-center mb-16" data-aos="fade-up">
            <h2 className={`text-5xl md:text-6xl text-center mb-4 uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-white dark:via-gray-200 dark:to-gray-400 py-4 font-black leading-normal`}>
              {t.title}
            </h2>
        </div>

        {/* Main Skills Container */}
        <div className="bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-2xl shadow-sm p-8 border border-slate-200/60 dark:border-zinc-800 transform transition-all duration-500 hover:shadow-xl mb-12" data-aos="fade-up">
          <div className="flex flex-col gap-12 items-center w-full">
            {/* Top: Skills List in 2 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 w-full">
              {Object.entries(skillCategories).map(([category, skills], categoryIndex) => (
                <div
                  key={category}
                  className={`animate-[fade-in-up_0.5s_ease-out] p-3 rounded-2xl transition-all duration-300 ease-out border ${
                    hoveredCategory === category
                      ? 'bg-blue-50/40 dark:bg-zinc-800/50 border-slate-200/80 dark:border-zinc-700/80 shadow-sm translate-x-1'
                      : 'bg-transparent border-transparent translate-x-0'
                  }`}
                  style={{animationDelay: `${categoryIndex * 100}ms`, animationFillMode: 'both'}}
                  onMouseEnter={() => setHoveredCategory(category)}
                  onMouseLeave={() => setHoveredCategory(null)}
                >
                  <div className="flex items-center mb-3">
                    <h4 className={`text-lg font-semibold transition-colors duration-300 ${
                      hoveredCategory === category
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-800 dark:text-white'
                    }`}>
                      {t[category as keyof typeof t]}
                    </h4>
                  </div>
                  <div className="ml-2">
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill, skillIndex) => (
                        <div
                          key={skillIndex}
                          className="flex items-center gap-1 px-3 py-1 bg-blue-100/80 dark:bg-blue-900/80 text-blue-700 dark:text-blue-300 rounded-full text-sm hover:scale-105 transition-transform whitespace-nowrap"
                        >
                          {skill.icon}
                          <span>{skill.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>


          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-8">
          <div className="bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-2xl shadow-sm p-8 border border-slate-200/60 dark:border-zinc-800" data-aos="fade-up">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-6 pb-2 border-b-2 border-blue-200 dark:border-blue-800 flex items-center">
              <Award size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
              {t.certificates}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {certificates.map((cert, index) => (
                <div 
                  key={index} 
                  className="p-5 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 dark:border-zinc-800 shadow-sm transition-all duration-300 bounce-pop-effect hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:hover:shadow-[0_0_30px_rgba(96,165,250,0.5)]"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <h4 className="font-medium text-gray-800 dark:text-white">{cert.name}</h4>
                  <div className="flex justify-between mt-1 mb-2">
                    <span className="text-gray-600 dark:text-gray-300 text-sm">{cert.issuer}</span>
                    <span className="text-blue-600 dark:text-blue-400 text-sm">{cert.year}</span>
                  </div>
                  
                  {cert.url && (
                    <a 
                      href={cert.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors mt-1 bg-blue-50/80 dark:bg-blue-900/30 px-3 py-1 rounded-full"
                    >
                      <ExternalLink size={14} className="mr-1" />
                      {t.viewCertificate}
                    </a>
                  )}
                </div>
              ))}
            </div>
            
            <div className="w-full max-w-2xl mx-auto mt-6">
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-3 flex items-center justify-center">
                  <Lightbulb size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                  {t.inspirationalQuotes}
                </h4>
                
                <div className="relative">
                  <button 
                    onClick={() => navigateQuote('prev')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -ml-2 p-1 bg-white/80 dark:bg-black/80 rounded-full shadow-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all duration-500 z-10"
                    aria-label={t.previous}
                  >
                    <ChevronLeft size={20} className="text-blue-600 dark:text-blue-400" />
                  </button>
                  
                  <div className="text-center p-3 bg-white/70 dark:bg-black/70 rounded-lg shadow-sm mx-8 transition-all duration-1000 min-h-[140px] flex flex-col justify-center">
                    <p className="text-gray-800 dark:text-white font-medium italic mb-2">"{language === 'vi' ? techQuotes[activeQuote].textVi : techQuotes[activeQuote].textEn}"</p>
                    <p className="text-blue-600 dark:text-blue-400 text-sm">— {techQuotes[activeQuote].author}</p>
                  </div>
                  
                  <button 
                    onClick={() => navigateQuote('next')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 -mr-2 p-1 bg-white/80 dark:bg-black/80 rounded-full shadow-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all duration-500 z-10"
                    aria-label={t.next}
                  >
                    <ChevronRight size={20} className="text-blue-600 dark:text-blue-400" />
                  </button>
                </div>
                
                <div className="flex justify-center mt-3 space-x-1">
                  {techQuotes.slice(0, 5).map((_, index) => (
                    <button 
                      key={index}
                      onClick={() => setActiveQuote(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-500 ${
                        activeQuote === index 
                          ? 'bg-blue-600 dark:bg-blue-400 w-4' 
                          : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                      }`}
                      aria-label={`Quote ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(Skills);
